import apiClient from './client';
import type {
  ServiceEvent,
  ServiceTemplate,
  CalendarEventsParams,
  CreateEventData,
  UpdateEventData,
  CreateTemplateData,
  UpdateTemplateData,
  GenerateResponse,
  CalendarFeedToken,
} from '@/types/calendar';

function unwrap<T>(data: T | { data: T }): T {
  if (data && typeof data === 'object' && 'data' in data && !Array.isArray((data as { data: T }).data)) {
    return (data as { data: T }).data;
  }
  return data as T;
}

function unwrapArray<T>(data: unknown): T[] {
  if (data && typeof data === 'object' && 'data' in (data as Record<string, unknown>)) {
    return (data as { data: T[] }).data;
  }
  return data as T[];
}

export async function getCalendarEvents(
  params?: CalendarEventsParams,
): Promise<ServiceEvent[]> {
  const response = await apiClient.get('/calendar/events', { params });
  return unwrapArray<ServiceEvent>(response.data);
}

export async function getCalendarEvent(id: string): Promise<ServiceEvent> {
  const response = await apiClient.get(`/calendar/events/${id}`);
  return unwrap<ServiceEvent>(response.data);
}

export async function createEvent(data: CreateEventData): Promise<ServiceEvent> {
  const response = await apiClient.post('/calendar/events', data);
  return unwrap<ServiceEvent>(response.data);
}

export async function updateEvent(
  id: string,
  data: UpdateEventData,
): Promise<ServiceEvent> {
  const response = await apiClient.put(`/calendar/events/${id}`, data);
  return unwrap<ServiceEvent>(response.data);
}

export async function cancelEvent(id: string): Promise<ServiceEvent> {
  const response = await apiClient.delete(`/calendar/events/${id}`);
  return unwrap<ServiceEvent>(response.data);
}

export async function generateMonth(month: string): Promise<GenerateResponse> {
  const response = await apiClient.post('/calendar/events/generate', { month });
  return response.data;
}

export async function getTemplates(): Promise<ServiceTemplate[]> {
  const response = await apiClient.get('/calendar/templates');
  return unwrapArray<ServiceTemplate>(response.data);
}

export async function createTemplate(data: CreateTemplateData): Promise<ServiceTemplate> {
  const response = await apiClient.post('/calendar/templates', data);
  return unwrap<ServiceTemplate>(response.data);
}

export async function updateTemplate(
  id: string,
  data: UpdateTemplateData,
): Promise<ServiceTemplate> {
  const response = await apiClient.put(`/calendar/templates/${id}`, data);
  return unwrap<ServiceTemplate>(response.data);
}

export async function deactivateTemplate(id: string): Promise<ServiceTemplate> {
  const response = await apiClient.delete(`/calendar/templates/${id}`);
  return unwrap<ServiceTemplate>(response.data);
}

export async function downloadIcs(eventId: string): Promise<Blob> {
  const response = await apiClient.get(`/calendar/events/${eventId}/ics`, {
    responseType: 'blob',
  });
  return response.data as Blob;
}

export async function regenerateFeedToken(): Promise<CalendarFeedToken> {
  const response = await apiClient.post('/calendar/feed/regenerate');
  return unwrap<CalendarFeedToken>(response.data);
}
